import {IProductImage} from "../../models/Search";
import React, {useEffect, useRef} from "react";
import {IconButton, Image, ImageFit} from "@fluentui/react";
import {cloneDeep} from "lodash";
import {useUploadImageMutation} from "../../store/Api";

interface IStorefrontBundleImagesProps {
    selected: string;
    images: IProductImage[]
    onSelect: (imageUrl: string) => void;
}

const CARD_WIDTH = 140;
const CARD_GAP = 10;

export const StorefrontBundleImages: React.FC<IStorefrontBundleImagesProps> = (props) => {
    const [images, setImages] = React.useState<IProductImage[]>([]);
    const [uploadImage] = useUploadImageMutation();
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const [leftHover, setLeftHover] = React.useState(false);
    const [rightHover, setRightHover] = React.useState(false);
    const [uploadHover, setUploadHover] = React.useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const completeImageList: IProductImage[] = cloneDeep(props.images);
        if (props.selected !== "" && !props.images.some(i => i.url === props.selected)) {
            completeImageList.push({
                name: "Bundle Image",
                url: props.selected,
                type: "Custom",
                id: '',
                isCustom: true
            });
        }
        setImages(completeImageList);
    }, [props.selected, props.images]);

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [images]);

    const checkScroll = () => {
        const el = scrollContainerRef.current;
        if (el) {
            setCanScrollLeft(el.scrollLeft > 0);
            setCanScrollRight(el.scrollLeft + el.clientWidth + 1 < el.scrollWidth);
        }
    };

    const scrollByCard = (direction: 'left' | 'right') => {
        scrollContainerRef.current?.scrollBy({
            left: direction === 'left' ? -(CARD_WIDTH + CARD_GAP) : (CARD_WIDTH + CARD_GAP),
            behavior: 'smooth'
        });
    };

    const inputFile = useRef<HTMLInputElement | null>(null);

    const onChangeFile = (event: any) => {
        event.stopPropagation();
        event.preventDefault();
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async () => {
                const buf = reader.result;
                if (buf && typeof buf !== "string" && buf.byteLength < 20971520) {
                    const newId = crypto.randomUUID();
                    const newImages = cloneDeep(props.images) ?? [];
                    uploadImage({imageId: newId, file: buf, imageType: 'Custom'})
                        .unwrap()
                        .then((result) => {
                            newImages.push({name: "Bundle Image", type: "Custom", url: result.url, id: newId, isCustom: true});
                            setImages(newImages);
                            props.onSelect(result.url);
                        });
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    return (
        <div style={{paddingTop: '1em'}}>
            <div style={{
                position: 'relative',
                background: '#f5f6fa',
                borderRadius: 8,
                padding: '16px 24px',
            }}>
                <div style={{position: 'relative', width: '100%'}}>
                    {/* Left Arrow */}
                    {canScrollLeft && (
                        <IconButton
                            iconProps={{iconName: 'ChevronLeft'}}
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 32,
                                height: 160,
                                zIndex: 1,
                                background: leftHover ? 'rgba(120,156,47,0.75)' : 'rgba(255,255,255,0.7)'
                            }}
                            onClick={() => scrollByCard('left')}
                            ariaLabel="Scroll left"
                            onMouseEnter={() => setLeftHover(true)}
                            onMouseLeave={() => setLeftHover(false)}
                        />
                    )}

                    {/* Scrollable row */}
                    <div
                        ref={scrollContainerRef}
                        onScroll={checkScroll}
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            overflowX: 'auto',
                            gap: CARD_GAP,
                            padding: '8px 0',
                            scrollBehavior: 'smooth',
                            // hide native scrollbar
                            msOverflowStyle: 'none',
                        }}
                    >
                        {/* Upload Pod */}
                        <div
                            onMouseEnter={() => setUploadHover(true)}
                            onMouseLeave={() => setUploadHover(false)}
                            onClick={() => inputFile?.current?.click()}
                            title="Upload New Image"
                            style={{
                                flexShrink: 0,
                                width: uploadHover ? CARD_WIDTH : 48,
                                minWidth: uploadHover ? CARD_WIDTH : 48,
                                maxWidth: uploadHover ? CARD_WIDTH : 48,
                                height: 140,
                                borderRadius: 5,
                                border: uploadHover ? '2px dashed rgb(120,156,47)' : '2px dashed #ddd',
                                background: uploadHover ? 'rgba(120,156,47,0.05)' : 'transparent',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                overflow: 'hidden',
                                transition: 'width 0.2s, min-width 0.2s, max-width 0.2s, border 0.15s, background 0.15s',
                                boxSizing: 'border-box',
                            }}
                        >
                            <IconButton
                                iconProps={{iconName: 'Upload'}}
                                styles={{
                                    root: {width: 28, height: 28, pointerEvents: 'none', flexShrink: 0},
                                    icon: {fontSize: 18, color: uploadHover ? 'rgb(120,156,47)' : '#aaa'}
                                }}
                            />
                            {uploadHover && (
                                <span style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: 'rgb(120,156,47)',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                }}>
                                    Upload New
                                </span>
                            )}
                        </div>
                        {images.map((image, index) => {
                            const isSelected = props.selected === image.url;
                            return (
                                <div
                                    key={`img-${index}`}
                                    onClick={() => props.onSelect(image.url)}
                                    style={{
                                        flexShrink: 0,
                                        width: CARD_WIDTH,
                                        minWidth: CARD_WIDTH,
                                        maxWidth: CARD_WIDTH,
                                        height: 140,
                                        borderRadius: 5,
                                        border: isSelected ? '3px solid rgb(120,156,47)' : '1px solid #ccc',
                                        backgroundColor: isSelected ? 'rgb(220,237,200)' : '#fff',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        padding: 6,
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <Image
                                        src={image.url}
                                        alt={image.name}
                                        imageFit={ImageFit.contain}
                                        styles={{
                                            root: {width: '100%', height: '100%'},
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {/* Right Arrow */}
                    {canScrollRight && (
                        <IconButton
                            iconProps={{iconName: 'ChevronRight'}}
                            style={{
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 32,
                                height: 160,
                                zIndex: 1,
                                background: rightHover ? 'rgba(120,156,47,0.75)' : 'rgba(255,255,255,0.7)'
                            }}
                            onClick={() => scrollByCard('right')}
                            ariaLabel="Scroll right"
                            onMouseEnter={() => setRightHover(true)}
                            onMouseLeave={() => setRightHover(false)}
                        />
                    )}
                </div>
            </div>

            <input type='file' id='file' ref={inputFile} accept="image/*"
                   style={{display: 'none'}} onChange={onChangeFile}/>
        </div>
    );
}